import classNames from "classnames";
import React, {
    CSSProperties,
    type JSX,
    memo,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import SVG from "react-inlinesvg";
import { PromoAd } from "tsi-common-react/src/blocks/components/PromoAd";
import { Modal, ModalStyles } from "tsi-common-react/src/common/Modal";
import {
    OnResizeHandler,
    WindowResizeSensor,
} from "tsi-common-react/src/common/WindowResizeSensor";
import { BreakPoint } from "tsi-common-react/src/models/screen.interfaces";
import {
    getViewportBreakpoint,
    isMobile,
} from "tsi-common-react/src/utils/detectMobile";
import { focusElement } from "tsi-common-react/src/utils/keyboardFocus";
import { useIsSSR } from "tsi-common-react/src/utils/ssr";
import { t } from "ttag";

import iconBars from "../../svg/bars.svg";
import iconCaretDown from "../../svg/caret-down.svg";
import iconX from "../../svg/x.svg";
import {
    IMenuCategoryLink,
    IMenuItem,
    IMenuPageLinkWithImage,
    IMenuSnippetLink,
} from "../menu.interface";
import { CategoryLink } from "./menu/CategoryLink";
import { DropdownLinks } from "./menu/DropdownLinks";
import { MenuAdverts } from "./menu/MenuAdverts";
import { NavFooter } from "./menu/NavFooter";
import { PageLink } from "./menu/PageLink";

interface IProps {
    menuItems: IMenuItem[];
    dropdownOffsetTop?: number;
}

interface IMenuList {
    menuItems: IMenuItem[];
    isMobile: boolean;
    onOpenModal: (
        event?: React.MouseEvent<HTMLElement>,
        subCategory?: IMenuCategoryLink,
    ) => void;
    openedLink: string;
}

// Memoized sub-components
const MobileTriggerButton = memo(
    ({ onOpenModal }: { onOpenModal: () => void }) => (
        <button
            tabIndex={0}
            className="mobile-toggle"
            aria-label={t`Open Menu`}
            title={t`Open Menu`}
            onClick={onOpenModal}
        >
            <SVG
                className="site-nav__mobile-toggle-icon"
                src={iconBars}
                title={t`Menu Icon`}
                aria-hidden="true"
            />
        </button>
    ),
);

const CloseButton = memo(({ onClose }: { onClose: () => void }) => (
    <button
        aria-label={t`Close Menu`}
        onClick={onClose}
        className="main-dropdown-menu__close"
    >
        <SVG
            className="main-dropdown-menu__close-icon"
            src={iconX}
            aria-hidden="true"
        />
    </button>
));

const BackButton = memo(({ onBack }: { onBack: () => void }) => (
    <button
        title={t`Back`}
        className="main-dropdown-menu__back"
        onClick={onBack}
    >
        <SVG
            className="main-dropdown-menu__back-icon"
            src={iconCaretDown}
            title={t`Dropdown Menu Icon`}
            aria-hidden="true"
        />
        {t`Back`}
    </button>
));

const MenuList = memo(
    ({ menuItems, isMobile, onOpenModal, openedLink }: IMenuList) => {
        const menuLinks = useMemo(() => {
            let index = 0;
            return menuItems.reduce<JSX.Element[]>((memo, menuItem) => {
                switch (menuItem.type) {
                    case "nav_category":
                        const link = (
                            <li key={menuItem.value.title}>
                                <CategoryLink
                                    linkObject={menuItem}
                                    isMobile={isMobile}
                                    linkID={index++}
                                    openModal={onOpenModal}
                                    openedLink={openedLink}
                                />
                            </li>
                        );
                        return memo.concat(link);
                    case "page_link":
                        return memo.concat(
                            <li key={menuItem.value.override_title}>
                                <PageLink
                                    linkObject={menuItem}
                                    isMobile={isMobile}
                                    linkID={index++}
                                />
                            </li>,
                        );
                    case "nav_advert":
                        if (isMobile) {
                            return memo.concat(
                                <li key={`advert-${index++}`}>
                                    <div className="main-dropdown-menu__adverts">
                                        <PromoAd promoArrayEl={menuItem} />
                                    </div>
                                </li>,
                            );
                        }
                        break;
                }
                return memo;
            }, []);
        }, [menuItems, isMobile, onOpenModal, openedLink]);

        return <ul className="main-menu-list">{menuLinks}</ul>;
    },
);

const DropdownContent = memo(
    ({
        activeMenuItem,
        isMobile,
    }: {
        activeMenuItem: IMenuCategoryLink | null;
        isMobile: boolean;
    }) => {
        const dropdownLinks = useMemo(() => {
            if (!activeMenuItem) return null;
            return (
                <DropdownLinks
                    isActive={true}
                    isMobile={isMobile}
                    linksArray={activeMenuItem.value.sub_nav.filter(
                        (
                            val,
                        ): val is IMenuPageLinkWithImage | IMenuSnippetLink => {
                            return (
                                val.type === "page_link_with_image" ||
                                val.type === "snippet_link"
                            );
                        },
                    )}
                    linkID={0}
                    key={0}
                />
            );
        }, [activeMenuItem, isMobile]);
        return dropdownLinks;
    },
);

// Custom hook for promo ads and nav footer
const usePromoAdsAndFooter = (activeMenuItem: IMenuCategoryLink | null) => {
    return useMemo(() => {
        const ads: JSX.Element[] = [];
        let footer: JSX.Element | null = null;
        let isDefaultAdAdded = false;

        if (activeMenuItem && activeMenuItem.value.sub_nav.length > 0) {
            activeMenuItem.value.sub_nav.forEach((val, i) => {
                switch (val.type) {
                    case "nav_advert":
                        if (val.value?.promo_period?.is_promo_active) {
                            if (isDefaultAdAdded && ads.length > 0) {
                                ads.pop();
                            }
                            ads.push(<PromoAd key={i} promoArrayEl={val} />);
                        } else if (
                            ads.length === 0 &&
                            (val.value?.promo_period === undefined ||
                                val.value?.promo_period === null ||
                                !val.value?.promo_period?.is_promo_active)
                        ) {
                            ads.push(<PromoAd key={i} promoArrayEl={val} />);
                            isDefaultAdAdded = true;
                        }
                        break;
                    case "nav_footer":
                        footer = <NavFooter navFooterEl={val} />;
                        break;
                }
            });
        }

        return { promoAds: ads, navFooter: footer };
    }, [activeMenuItem]);
};

export const MainMenu: React.FC<IProps> = ({
    menuItems,
    dropdownOffsetTop,
}) => {
    const isSSR = useIsSSR();
    // State
    const [isMobileState, setIsMobileState] = useState(false);
    const [iframeDocHeight, setIframeDocHeight] = useState<number | null>(null);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [activeMenuItem, setActiveMenuItem] =
        useState<IMenuCategoryLink | null>(null);
    const [submenuParentElement, setSubmenuParentElement] =
        useState<HTMLElement | null>(null);
    const [openedLink, setOpenedLink] = useState("");

    // Refs
    const iframeHeightCallbackSet = useRef(false);
    const timeoutRefs = useRef<Set<NodeJS.Timeout>>(new Set());

    const isMobileScreenWidth =
        !isSSR && getViewportBreakpoint() < BreakPoint.MEDIUM;

    const setIframeHeightCallback = useCallback(() => {
        if (isSSR || iframeHeightCallbackSet.current) {
            return;
        }
        if (!window.parentIFrame) {
            return;
        }
        window.parentIFrame.getPageInfo((info) => {
            setIframeDocHeight(info.windowHeight);
        });
        iframeHeightCallbackSet.current = true;
    }, [isSSR]);

    // Cleanup timeouts on unmount
    useEffect(() => {
        return () => {
            timeoutRefs.current.forEach(clearTimeout);
            timeoutRefs.current.clear();
        };
    }, []);

    // Safe timeout wrapper
    const setSafeTimeout = useCallback(
        (callback: () => void, delay: number) => {
            const timeoutId = setTimeout(() => {
                callback();
                timeoutRefs.current.delete(timeoutId);
            }, delay);
            timeoutRefs.current.add(timeoutId);
            return timeoutId;
        },
        [],
    );

    // Event handlers
    const onOpenModal = useCallback(
        (
            event?: React.MouseEvent<HTMLElement>,
            subCategory?: IMenuCategoryLink,
        ) => {
            let menuLinkElem: HTMLElement | null = null;
            if (event) {
                event.preventDefault();
                menuLinkElem = event.currentTarget;
            }
            if (!isSSR && menuLinkElem) {
                if (
                    !menuLinkElem.classList.contains(
                        "site-nav__main-menu-item--mobile",
                    )
                ) {
                    document.body.classList.add("body-dropdown-open--desktop");
                } else {
                    const layout = document.querySelector(".layout");
                    if (layout) {
                        layout.classList.add("layout--hidden");
                    }
                }
            }
            setModalIsOpen(true);
            setActiveMenuItem(subCategory || null);
            setSubmenuParentElement(menuLinkElem);
            setOpenedLink(subCategory?.value.title || "");

            if (subCategory && !isSSR) {
                setSafeTimeout(() => {
                    focusElement(".main-dropdown-menu__back");
                }, 300);
            }
        },
        [isSSR, setSafeTimeout],
    );

    const onCloseModal = useCallback(
        (event?: React.MouseEvent<Element> | React.KeyboardEvent<Element>) => {
            let menuLinkElem: HTMLElement | null = null;
            if (event) {
                event.preventDefault();
                menuLinkElem = event.currentTarget as HTMLElement;
            }

            if (!isSSR && menuLinkElem) {
                if (
                    !menuLinkElem.classList.contains(
                        "site-nav__main-menu-item--mobile",
                    )
                ) {
                    document.body.classList.remove(
                        "body-dropdown-open--desktop",
                    );
                    const layout = document.querySelector(".layout--hidden");
                    if (layout) {
                        layout.classList.remove("layout--hidden");
                    }
                }
            }
            setModalIsOpen(false);
            setActiveMenuItem(null);
            setOpenedLink("");
        },
        [isSSR],
    );

    const onCloseSubNav = useCallback(() => {
        const parentElem = submenuParentElement;
        setSubmenuParentElement(null);
        setActiveMenuItem(null);

        if (parentElem && !isSSR) {
            setSafeTimeout(() => {
                parentElem.focus();
            }, 300);
        }
    }, [submenuParentElement, isSSR, setSafeTimeout]);

    const onResize: OnResizeHandler = useCallback(
        (event) => {
            if (event.type === "height") return;

            const isMobileNow = isMobileScreenWidth;
            if (isMobileNow !== isMobileState) {
                setIsMobileState(isMobileNow);
                if (modalIsOpen) {
                    onCloseModal();
                }
            }

            if (isMobileNow && !isSSR && isMobile.Android()) {
                return;
            }
        },
        [isMobileScreenWidth, isMobileState, modalIsOpen, onCloseModal, isSSR],
    );

    useEffect(() => {
        if (isSSR) return;

        const initialMobile = isMobileScreenWidth;
        if (initialMobile !== isMobileState) {
            setIsMobileState(initialMobile);
        }

        setIframeHeightCallback();
    }, [isSSR, isMobileScreenWidth, isMobileState, setIframeHeightCallback]);
    // Memoized values
    const modalStyle: ModalStyles = useMemo(() => {
        let contentPosition: CSSProperties["position"];
        let contentTop: CSSProperties["top"];
        let contentHeight: CSSProperties["height"];
        if (isMobileState) {
            contentPosition = "fixed";
            contentTop = "0px";
            contentHeight = iframeDocHeight ? `${iframeDocHeight}px` : "100%";
        } else {
            contentPosition = "relative";
            contentTop = `${(dropdownOffsetTop ?? 0) - 20}px`;
            contentHeight = "auto";
        }
        return {
            overlay: {
                position: "absolute",
                backgroundColor: "rgba(255, 255, 255, 0)",
            },
            content: {
                position: contentPosition,
                top: contentTop,
                marginTop: "0",
                width: "100%",
                height: contentHeight,
                overflow: "hidden",
                border: "none",
                boxShadow: "0 10px 10px -5px rgba(0,0,0,.3)",
            },
        };
    }, [isMobileState, iframeDocHeight, dropdownOffsetTop]);

    const activeMenuLabel = useMemo(() => {
        if (activeMenuItem) {
            return t`${activeMenuItem.value.title} Menu`;
        }
        return "";
    }, [activeMenuItem]);

    const dropdownContentClasses = useMemo(
        () =>
            classNames({
                "main-dropdown-menu__content": true,
                "main-dropdown-menu__content--closed": activeMenuItem,
                "l-capped-width": true,
            }),
        [activeMenuItem],
    );

    const dropdownSubContentClasses = useMemo(
        () =>
            classNames({
                "main-dropdown-menu__sub-content": true,
                "main-dropdown-menu__sub-content--open": activeMenuItem,
                "l-capped-width": true,
            }),
        [activeMenuItem],
    );

    // Get promo ads and nav footer
    const { promoAds, navFooter } = usePromoAdsAndFooter(activeMenuItem);

    const closeBtnPropsDesktop = useMemo(
        () => ({
            style: {
                top: "1.5rem",
                right:
                    !isSSR && window.innerWidth >= 1200
                        ? "calc(calc(100% - 1200px) / 2 - 20px)"
                        : "1.5rem",
            },
        }),
        [isSSR],
    );

    const closeBtnPropsMobile = useMemo(
        () => ({
            style: {
                top: "0.5rem",
                right: "0.5rem",
            },
        }),
        [],
    );

    if (isMobileState) {
        return (
            <>
                <WindowResizeSensor onResize={onResize} />
                <MobileTriggerButton onOpenModal={() => onOpenModal()} />
                <Modal
                    aria={{ modal: true }}
                    contentLabel={activeMenuLabel}
                    isOpen={modalIsOpen}
                    style={modalStyle}
                    onRequestClose={onCloseModal}
                    role="dialog"
                    onAfterOpen={() => {
                        if (!isSSR) {
                            focusElement(
                                ".main-dropdown-menu .main-dropdown-menu__close",
                            );
                        }
                    }}
                    closeBtnProps={closeBtnPropsMobile}
                >
                    <h1 className="ada-screenreader-only">{t`Shop Menu`}</h1>
                    <div className="main-dropdown-menu">
                        <CloseButton onClose={onCloseModal} />
                        <div
                            id="main-menu"
                            className={dropdownContentClasses}
                            aria-expanded={!activeMenuItem}
                            aria-label={t`Mattresses, Bed Bases, Pillows, Bedding, and More From Tempur`}
                        >
                            <MenuList
                                menuItems={menuItems}
                                isMobile={isMobileState}
                                onOpenModal={onOpenModal}
                                openedLink={openedLink}
                            />
                        </div>
                        <div
                            className={dropdownSubContentClasses}
                            aria-expanded={!!activeMenuItem}
                            id="menu-subnav"
                        >
                            <BackButton onBack={onCloseSubNav} />
                            <DropdownContent
                                activeMenuItem={activeMenuItem}
                                isMobile={isMobileState}
                            />
                            <MenuAdverts promos={promoAds} />
                        </div>
                        {navFooter}
                    </div>
                </Modal>
            </>
        );
    }

    return (
        <>
            <WindowResizeSensor onResize={onResize} />
            <MenuList
                menuItems={menuItems}
                isMobile={isMobileState}
                onOpenModal={onOpenModal}
                openedLink={openedLink}
            />
            <Modal
                contentLabel={activeMenuLabel}
                isOpen={modalIsOpen}
                style={modalStyle}
                onRequestClose={onCloseModal}
                closeBtnProps={closeBtnPropsDesktop}
            >
                <h1 className="ada-screenreader-only">{t`Shop Menu`}</h1>
                <div className="main-dropdown-menu">
                    <div
                        id="main-menu"
                        className={dropdownContentClasses}
                        aria-expanded={!!activeMenuItem}
                        aria-label={t`Mattresses, Bed Bases, Pillows, Bedding, and More From Tempur`}
                    >
                        <DropdownContent
                            activeMenuItem={activeMenuItem}
                            isMobile={isMobileState}
                        />
                        <MenuAdverts promos={promoAds} key={promoAds.length} />
                    </div>
                    {navFooter}
                </div>
            </Modal>
        </>
    );
};
